import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { LoadMoreButton as LoadingMoreButton } from '@sorare/core/src/atoms/buttons/LoadMoreButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { Bid } from '../Bid';
import type {
  BidHistory_bidToEarnReward,
  BidHistory_tokenBidConnection,
} from './__generated__/index.graphql';

const Button = styled.div`
  align-self: center;
  padding-bottom: var(--double-unit);
`;

interface Props {
  loadMoreBids: (
    reload: boolean,
    variable: { bidCursor: string }
  ) => Promise<unknown>;
  bids: BidHistory_tokenBidConnection;
  bidToEarnRewards?: BidHistory_bidToEarnReward[] | null;
  loading: boolean;
  skipFirst?: boolean;
  displayAbsoluteDate?: boolean;
}

export const BidHistory = ({
  bids,
  bidToEarnRewards,
  loading,
  loadMoreBids,
  skipFirst = false,
  displayAbsoluteDate = false,
}: Props) => {
  const { hasNextPage, endCursor } = bids.pageInfo;

  if (bids.nodes.length === 0) return null;

  const bidRewardsMap = (bidToEarnRewards ?? []).reduce<
    Record<string, BidHistory_bidToEarnReward['reward'][]>
  >((acc, bidToEarnReward) => {
    if (bidToEarnReward.bid) {
      acc[bidToEarnReward.bid.id] = acc[bidToEarnReward.bid.id]
        ? [...acc[bidToEarnReward.bid.id], bidToEarnReward.reward]
        : [bidToEarnReward.reward];
    }
    return acc;
  }, {});

  const list = skipFirst ? bids.nodes.slice(1) : bids.nodes;

  return (
    <Vertical gap={2}>
      <div>
        {list.map(bid => (
          <Bid
            bid={bid}
            key={bid.id}
            displayAbsoluteDate={displayAbsoluteDate}
            bidRewards={bidRewardsMap[bid.id]}
          />
        ))}
      </div>
      {!!hasNextPage && (
        <Button>
          <LoadingMoreButton
            hasMore={hasNextPage}
            loading={loading}
            loadMore={() => {
              loadMoreBids(false, { bidCursor: endCursor! });
            }}
          />
        </Button>
      )}
    </Vertical>
  );
};

BidHistory.fragments = {
  bid: gql`
    fragment BidHistory_tokenBidConnection on TokenBidConnection {
      nodes {
        id
        ...Bid_tokenBid
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
    ${Bid.fragments.tokenBid}
  ` as TypedDocumentNode<BidHistory_tokenBidConnection>,
  bidToEarnReward: gql`
    fragment BidHistory_bidToEarnReward on BidToEarnReward {
      id
      bid {
        id
      }
      reward {
        id
        ...Bid_AnyRewardInterface
      }
    }
    ${Bid.fragments.AnyRewardInterface}
  ` as TypedDocumentNode<BidHistory_bidToEarnReward>,
};
